import { Link, useLocation } from '@tanstack/react-router'
import { ArrowRightIcon, LoaderCircleIcon, XIcon } from 'lucide-react'

import { Button } from '@/components/ui'
import {
  useGettingStartedDismissed,
  useGettingStartedSteps,
  useShowGettingStartedNudges,
} from '../hooks'
import { nextOpenStep } from '../steps'
import { useGettingStartedStore } from '../store'
import {
  type TourTarget,
  useTourTarget,
  useTrackTourProgress,
} from '../tourProgress'
import { StepAction } from './StepAction'
import { TourProgress } from './TourProgress'

export const GETTING_STARTED_PATH = '/getting-started'

export const useShowGettingStartedBar = () => {
  const showNudges = useShowGettingStartedNudges()
  const { pathname } = useLocation()
  return showNudges && pathname !== GETTING_STARTED_PATH
}

// Follows the user around the app with the next tour step. It sits at the foot
// of the main area, so it never covers the sidebar or the chat panel, and it
// steps aside while a table's bulk actions bar is up.
export function GettingStartedBar() {
  useTrackTourProgress()
  const show = useShowGettingStartedBar()
  const steps = useGettingStartedSteps()
  const doneSteps = useGettingStartedStore((state) => state.doneSteps)
  const { dismiss } = useGettingStartedDismissed()
  const target = useTourTarget()
  const step = nextOpenStep(steps, doneSteps)

  if (!show || !step) return null

  const stepNumber = steps.indexOf(step) + 1

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-4 [body:has([data-bulk-actions-toolbar])_&]:hidden">
      <section
        aria-label="Getting started tour"
        className="pointer-events-auto flex w-full max-w-4xl items-center gap-4 rounded-xl border bg-white py-3 pr-2 pl-4 shadow-lg"
      >
        <TourProgress
          className="hidden sm:flex"
          total={steps.length}
          done={stepNumber - 1}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-blue-5">
            Step {stepNumber} of {steps.length}
          </p>
          <p className="truncate text-sm font-semibold text-blue-7">
            {step.title}
          </p>
        </div>
        <BarAction step={step} target={target} />
        <Link
          to={GETTING_STARTED_PATH}
          className="hidden shrink-0 cursor-pointer text-sm font-medium text-blue-6 hover:text-blue-7 sm:block"
        >
          All steps
        </Link>
        <Button
          theme="ghost"
          size="md"
          isIcon
          onClick={dismiss}
          aria-label="Close the getting started tour"
        >
          <XIcon />
        </Button>
      </section>
    </div>
  )
}

interface BarActionProps {
  step: NonNullable<ReturnType<typeof nextOpenStep>>
  target: TourTarget
}

function BarAction({ step, target }: BarActionProps) {
  if (step.action?.kind === 'create-project' && target.kind === 'no-project') {
    return <StepAction action={step.action} project={null} theme="default" />
  }

  if (target.kind === 'no-statements') {
    return (
      <Button asChild size="sm">
        <Link to="/projects/$slug/files" params={{ slug: target.project.slug }}>
          Add statements
          <ArrowRightIcon />
        </Link>
      </Button>
    )
  }

  if (target.kind === 'processing') {
    return (
      <p className="flex shrink-0 items-center gap-2 text-sm text-blue-6">
        <LoaderCircleIcon className="size-4 animate-spin text-brand-deep" />
        Your statements are processing
      </p>
    )
  }

  if (target.kind === 'no-project') {
    return (
      <Button asChild size="sm">
        <Link to="/projects">
          Open your projects
          <ArrowRightIcon />
        </Link>
      </Button>
    )
  }

  if (!step.action) return null
  return (
    <StepAction action={step.action} project={target.project} theme="default" />
  )
}
